<template>
  <!-- FormLabel renders a label cell and a field cell, so two columns means four. -->
  <div
    :class="
      columns === 2
        ? 'grid grid-cols-[130px_1fr_130px_1fr] gap-x-6 gap-y-3 items-center'
        : 'max-w-[867px] grid grid-cols-[minmax(30px,150px)_1fr] gap-4 items-center'
    "
  >
    <FormLabel label="Type" required>
      <RadioButton v-model="values.type" :options="typeOptions" />
    </FormLabel>
    <FormLabel label="Seller" required v-if="isQuote && isQuoteRAW">
      <Multiselect
        v-model="values.seller_id"
        :classes="{
          containerActive: 'ring-3 ring-hqPurple',
          optionSelected: 'bg-hqPurple text-white',
          optionSelectedPointed: 'bg-hqPurple text-white',
        }"
        :options="sellersOptions"
        placeholder="Choose a seller"
        searchable
      />
    </FormLabel>
    <FormLabel label="Transaction ID" :required="transactionIdRequired">
      <FormKit v-model="values.transactionId" name="transactionId" placeholder="Transaction ID" />
    </FormLabel>
    <FormLabel label="Store" required v-if="isQuote && isQuoteRAW">
      <ToggleButton v-model="values.store_id" :options="STORE_TYPES" />
    </FormLabel>
    <FormLabel label="HQ">
      <FormKit
        v-model="values.user_id"
        :options="HQ_TYPES"
        name="user_id"
        placeholder="HQ"
        type="select"
      />
    </FormLabel>
    <FormLabel label="Artist Team">
      <FormKit
        v-model="values.team_id"
        :options="artistTeamOptions"
        name="team_id"
        placeholder="Artist Team"
        type="select"
      />
    </FormLabel>
    <FormLabel label="Verify By">
      <FormKit
        v-model="values.verify_by"
        :options="VERIFY_BY_TYPES"
        name="verify_by"
        placeholder="Verify By"
        type="select"
      />
    </FormLabel>
    <FormLabel label="Currency" required>
      <RadioButton v-model="values.currency" :options="CURRENCY_TYPES" />
    </FormLabel>
    <FormLabel label="Name" required>
      <FormKit v-model="values.name" name="name" placeholder="Name" type="text" />
    </FormLabel>
    <FormLabel label="Email" required>
      <FormKit v-model="values.email" name="email" placeholder="Email" type="email" />
    </FormLabel>
    <FormLabel label="Phone" required>
      <FormKit v-model="values.phone" name="phone" placeholder="Phone" type="tel" />
    </FormLabel>
    <FormLabel label="Shipping" required>
      <div class="flex flex-col gap-4">
        <ToggleButton
          v-if="isShippingType"
          v-model="values.shipping_type"
          :options="SHIPPING_TYPES"
        />
        <FormKit
          v-model="values.service_id"
          :options="shippingServicesOptions"
          name="service_id"
          placeholder="Choose a shipping speed"
          type="select"
        />
      </div>
    </FormLabel>
    <FormLabel label="Address">
      <div class="flex gap-4">
        <FormKit
          v-model="values.address_line_1"
          name="small_country"
          placeholder="First line"
          type="text"
        />
        <FormKit
          v-model="values.address_line_2"
          name="small_country"
          placeholder="Second line"
          type="text"
        />
      </div>
    </FormLabel>
    <FormLabel label="City">
      <FormKit v-model="values.city" name="city" placeholder="City" type="text" />
    </FormLabel>
    <FormLabel label="State">
      <FormKit
        v-model="values.state_prov_region"
        name="state_prov_region"
        placeholder="State"
        type="text"
      />
    </FormLabel>
    <FormLabel label="ZIP Code">
      <FormKit v-model="values.postcode" name="small_country" placeholder="ZIP Code" type="text" />
    </FormLabel>
    <FormLabel label="Country">
      <FormKit
        v-model="values.country_id"
        :options="countriesOptions"
        name="country_id"
        placeholder="Choose a country"
        type="select"
      />
    </FormLabel>
  </div>
</template>
<script setup>
import { onMounted, toRefs, computed } from "vue";
import {
  ORDER_TYPES,
  STORE_TYPES,
  SHIPPING_TYPES,
  CURRENCY_TYPES,
  HQ_TYPES,
  VERIFY_BY_TYPES,
} from "@/consts/newOrderFormInputs";
import { useOptionsStoreActions } from "@/hooks/store/useOptionsStore";
import Multiselect from "@/components/UI/Form/Multiselect/Multiselect.vue";
import RadioButton from "../../../UI/Form/RadioButton/RadioButton.vue";
import FormLabel from "../../../UI/Form/FormLabel.vue";
import ToggleButton from "../../../UI/Form/Toggle/ToggleButton.vue";

const props = defineProps({
  values: {
    type: Object,
    default: () => ({}),
  },
  // Syncing a paid preview into an order needs the payment's transaction id.
  transactionIdRequired: {
    type: Boolean,
    default: false,
  },
  // The order types on offer, e.g. fewer where the rest make no sense (a synced preview
  // is a quick order entry or a quote).
  typeOptions: {
    type: Array,
    default: () => ORDER_TYPES,
  },
  // 2 puts two fields to a row, for tighter spaces such as a modal.
  columns: {
    type: Number,
    default: 1,
  },
});
const { values } = toRefs(props);

const {
  loadSellersOptions,
  loadCountriesOptions,
  loadShippingServicesOptions,
  loadArtistTeamOptions,
  sellersOptions,
  countriesOptions,
  shippingServicesOptions,
  artistTeamOptions,
} = useOptionsStoreActions();

const isShippingType = computed(() => {
  return values.value.type === ORDER_TYPES[3].value.toString();
});

const isQuote = computed(() => {
  return !(values.value.type === ORDER_TYPES[1].value.toString());
});

const isQuoteRAW = computed(() => {
  return !(values.value.type === ORDER_TYPES[2].value.toString());
});

onMounted(() => {
  loadSellersOptions();
  loadCountriesOptions();
  loadShippingServicesOptions();
  loadArtistTeamOptions();
});
</script>
<style scoped>
@reference "@/index.css";
.input-style {
  @apply flex items-center;
}

.label-style {
  @apply font-normal p-0 m-0;
}
</style>
